// Consent state from CookieYes, shared by the browser, the proxy, server
// components and the Stripe webhook. CookieYes (loaded through GTM) owns the
// banner and writes the `cookieyes-consent` cookie, e.g.
// "consentid:abc,consent:no,action:,necessary:yes,functional:no,
// analytics:no,performance:no,advertisement:no,other:no".
//
// Everything defaults to "no": a missing, empty or malformed cookie means the
// visitor has not consented (Danish/EU prior-consent rules).

export const COOKIEYES_CONSENT_COOKIE = "cookieyes-consent";

export interface TrackingConsent {
  /**
   * CookieYes "advertisement": ad pixels, ad cookies and server-side ad
   * events. Ad pixels and events also need production (see environment.ts).
   */
  advertisement: boolean;
  /** CookieYes "analytics": attribution cookies and persistent PostHog storage. */
  analytics: boolean;
}

export const NO_TRACKING_CONSENT: TrackingConsent = Object.freeze({
  advertisement: false,
  analytics: false,
});

interface CookieReader {
  get(name: string): { value: string } | undefined;
}

export function parseCookieYesConsent(
  value: string | null | undefined,
): TrackingConsent {
  if (!value) {
    return NO_TRACKING_CONSENT;
  }

  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // Keep the raw value; CookieYes normally writes it unencoded.
  }

  const categories = new Map<string, string>();
  for (const part of decoded.split(",")) {
    const separator = part.indexOf(":");
    if (separator <= 0) continue;
    const key = part.slice(0, separator).trim().toLowerCase();
    // First occurrence wins so an appended duplicate cannot flip a category.
    if (!categories.has(key)) {
      categories.set(key, part.slice(separator + 1).trim().toLowerCase());
    }
  }

  return {
    advertisement: categories.get("advertisement") === "yes",
    analytics: categories.get("analytics") === "yes",
  };
}

export function readTrackingConsent(cookieStore: CookieReader): TrackingConsent {
  return parseCookieYesConsent(cookieStore.get(COOKIEYES_CONSENT_COOKIE)?.value);
}

export type ConsentFlag = "yes" | "no";

function toConsentFlag(value: boolean): ConsentFlag {
  return value ? "yes" : "no";
}

/** Consent at checkout creation, stored on the Stripe session. */
export function getConsentStripeMetadata(
  consent: TrackingConsent,
): { consent_advertisement: ConsentFlag; consent_analytics: ConsentFlag } {
  return {
    consent_advertisement: toConsentFlag(consent.advertisement),
    consent_analytics: toConsentFlag(consent.analytics),
  };
}

/**
 * Consent recorded on a Stripe checkout session. Sessions created before the
 * consent fields existed have no value and count as "no".
 */
export function getConsentFromStripeMetadata(
  metadata: Record<string, string> | null | undefined,
): TrackingConsent {
  return {
    advertisement: metadata?.consent_advertisement === "yes",
    analytics: metadata?.consent_analytics === "yes",
  };
}
